import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { combineLatest, map, take } from 'rxjs';
import { removeParamFromUrl } from 'utilities';
import { ROUTE } from '../../feature-seatmap';
import { AuthStoreService } from './auth-store.service';

/** Reserved params that must not be blindly forwarded to avoid conflicts at the login route. */
const LOGIN_RESERVED_PARAMS = new Set(['returnUrl']);

function contextQueryParams(route: Parameters<CanActivateFn>[0]): Record<string, string> {
  const params: Record<string, string> = {};
  const keys = route.queryParamMap?.keys ?? Object.keys(route.queryParams ?? {});

  keys.forEach(key => {
    if (!LOGIN_RESERVED_PARAMS.has(key)) {
      const value = route.queryParamMap.get(key);
      if (value !== null) {
        params[key] = value;
        return;
      }

      const fallbackValue = route.queryParams?.[key];
      if (typeof fallbackValue === 'string') {
        params[key] = fallbackValue;
      }
    }
  });
  return params;
}

export const authGuard: CanActivateFn = (route, state) => {
  const authStoreService = inject(AuthStoreService);
  const router = inject(Router);
  const token = route.queryParamMap?.get('token');

  if (token) {
    return combineLatest([authStoreService.isAuthenticated$, authStoreService.accessToken$]).pipe(
      take(1),
      map(([{ isAuthenticated }, storedToken]) => {
        if (isAuthenticated && storedToken === token) {
          const cleanUrl = removeParamFromUrl(state.url, 'token') ?? state.url;
          return router.parseUrl(cleanUrl);
        }

        if (isAuthenticated) {
          // Authenticated user receiving a refreshed token should stay on the current route.
          return true;
        }

        return router.createUrlTree([ROUTE.login], {
          queryParams: { token, returnUrl: state.url, ...contextQueryParams(route) },
        });
      })
    );
  }

  return authStoreService.isAuthenticated$.pipe(
    take(1),
    map(({ isAuthenticated }) => {
      if (isAuthenticated) {
        return true;
      }

      return router.createUrlTree([ROUTE.login], {
        queryParams: { returnUrl: state.url, ...contextQueryParams(route) },
      });
    })
  );
};
